import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/lib/siteConfig";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function ImageField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = r.data.url.startsWith("http") ? r.data.url : `${BACKEND}${r.data.url}`;
      onChange(fullUrl);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="w-32 h-32 object-cover border border-zinc-800" />}
      <div className="flex gap-2">
        <input ref={ref} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`${testid}-file`} />
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="text-xs uppercase tracking-widest border border-zinc-700 px-3 py-2 hover:border-[var(--bs-red)] inline-flex items-center gap-2 disabled:opacity-50"
          data-testid={`${testid}-upload`}
        >
          <Upload size={12} /> {busy ? "Caricamento…" : "Carica"}
        </button>
      </div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="URL immagine"
        className="rounded-none bg-zinc-900 border-zinc-800 text-white text-xs"
        data-testid={`${testid}-url`}
      />
    </div>
  );
}

export default function AdminSettings() {
  const { config, refresh } = useSiteConfig();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [productCategories, setProductCategories] = useState([]);

  useEffect(() => {
    if (config && config.hero_title !== undefined) {
      setForm(JSON.parse(JSON.stringify(config)));
    }
  }, [config]);

  useEffect(() => {
    api.get("/products/categories").then((r) => setProductCategories(Array.isArray(r.data) ? r.data : [])).catch(() => setProductCategories([]));
  }, []);

  if (!form) return <div className="text-zinc-500">Caricamento…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero_title: form.hero_title || "",
        hero_subtitle: form.hero_subtitle || "",
        hero_image: form.hero_image || "",
        marquee_items: (form.marquee_items || []).map((s) => s.trim()).filter(Boolean),
        nav_categories: (form.nav_categories || []).filter((c) => c.label && c.value),
        contact_nav: { label: form.contact_nav?.label || "Dove siamo", position: Math.max(0, Number(form.contact_nav?.position ?? 1)) },
        home_categories: (form.home_categories || []).filter((c) => c.name && c.image),
        used_market: {
          enabled: !!form.used_market?.enabled,
          title: form.used_market?.title || "Mercatino dell'usato",
          subtitle: form.used_market?.subtitle || "",
          hero_image: form.used_market?.hero_image || "",
        },
        whatsapp_number: form.whatsapp_number || "",
        pickup_address: form.pickup_address || "",
        reservation_notice: form.reservation_notice || "",
      };
      await api.put("/site-config", payload);
      await refresh();
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const updateMarquee = (i, v) => {
    const arr = [...form.marquee_items];
    arr[i] = v;
    setForm({ ...form, marquee_items: arr });
  };
  const updateNav = (i, k, v) => {
    const arr = [...form.nav_categories];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, nav_categories: arr });
  };
  const updateHomeCat = (i, k, v) => {
    const arr = [...form.home_categories];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, home_categories: arr });
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-1">CMS</div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white">Contenuti sito</h1>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="btn-save-settings"
          className="bs-btn-primary px-6 py-3 text-xs uppercase tracking-widest font-bold disabled:opacity-60"
        >
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </button>
      </div>


      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <h2 className="font-display font-bold text-xl text-white">Prenotazioni WhatsApp</h2>
        <div><label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Numero WhatsApp con prefisso internazionale</label><Input value={form.whatsapp_number || ""} onChange={(e) => setForm({...form, whatsapp_number:e.target.value})} placeholder="393331234567" className="rounded-none bg-zinc-900 border-zinc-800 text-white" /></div>
        <div><label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Indirizzo ritiro</label><Input value={form.pickup_address || ""} onChange={(e) => setForm({...form, pickup_address:e.target.value})} className="rounded-none bg-zinc-900 border-zinc-800 text-white" /></div>
        <div><label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Avviso prenotazione</label><Textarea value={form.reservation_notice || ""} onChange={(e) => setForm({...form, reservation_notice:e.target.value})} className="rounded-none bg-zinc-900 border-zinc-800 text-white" /></div>
      </section>
      {/* HERO */}
      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <h2 className="font-display font-bold text-xl text-white">Hero (Home)</h2>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Titolo</label>
          <Input
            data-testid="cfg-hero-title"
            value={form.hero_title}
            onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
            className="rounded-none bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Sottotitolo</label>
          <Textarea
            data-testid="cfg-hero-subtitle"
            value={form.hero_subtitle}
            onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
            rows={3}
            className="rounded-none bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Immagine principale</label>
          <ImageField value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testid="cfg-hero-image" />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-white">Striscia scorrevole (marquee)</h2>
          <button
            onClick={() => setForm({ ...form, marquee_items: [...form.marquee_items, "NUOVO TESTO"] })}
            data-testid="btn-add-marquee"
            className="text-xs uppercase tracking-widest border border-zinc-700 px-3 py-2 hover:border-[var(--bs-red)] inline-flex items-center gap-1"
          >
            <Plus size={12} /> Aggiungi
          </button>
        </div>
        <div className="space-y-2">
          {form.marquee_items.map((m, i) => (
            <div key={i} className="flex gap-2" data-testid={`marquee-row-${i}`}>
              <Input
                value={m}
                onChange={(e) => updateMarquee(i, e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 text-white"
              />
              <button
                onClick={() => setForm({ ...form, marquee_items: form.marquee_items.filter((_, idx) => idx !== i) })}
                className="text-zinc-400 hover:text-[var(--bs-red)] px-3"
                data-testid={`btn-remove-marquee-${i}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* NAV CATEGORIES */}
      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-white">Voci del menù (header)</h2>
          <button
            onClick={() => setForm({ ...form, nav_categories: [...form.nav_categories, { label: "", value: "" }] })}
            data-testid="btn-add-nav"
            className="text-xs uppercase tracking-widest border border-zinc-700 px-3 py-2 hover:border-[var(--bs-red)] inline-flex items-center gap-1"
          >
            <Plus size={12} /> Aggiungi
          </button>
        </div>
        <div className="text-xs text-zinc-500">
          La <span className="text-white">label</span> è ciò che vede il cliente. Il <span className="text-white">valore</span> è la categoria del prodotto (deve corrispondere).
        </div>
        <div className="border border-zinc-800 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Nome voce pagina contatti</label>
            <Input value={form.contact_nav?.label || "Dove siamo"} onChange={(e) => setForm({ ...form, contact_nav: { ...(form.contact_nav || {}), label: e.target.value } })} className="rounded-none bg-zinc-900 border-zinc-800 text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Posizione nel menu</label>
            <Input type="number" min="0" value={form.contact_nav?.position ?? 1} onChange={(e) => setForm({ ...form, contact_nav: { ...(form.contact_nav || {}), position: Number(e.target.value) } })} className="rounded-none bg-zinc-900 border-zinc-800 text-white" />
            <p className="text-[11px] text-zinc-600 mt-1">0 = prima voce, 1 = dopo Home, 2 = dopo Catalogo, ecc.</p>
          </div>
        </div>
        {form.nav_categories.map((c, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`nav-row-${i}`}>
            <Input
              value={c.label}
              onChange={(e) => updateNav(i, "label", e.target.value)}
              placeholder="Etichetta visibile"
              className="rounded-none bg-zinc-900 border-zinc-800 text-white"
              data-testid={`nav-label-${i}`}
            />
            <Input
              value={c.value}
              onChange={(e) => updateNav(i, "value", e.target.value)}
              placeholder="Categoria (es. Arredamento)"
              className="rounded-none bg-zinc-900 border-zinc-800 text-white"
              data-testid={`nav-value-${i}`}
            />
            <button
              onClick={() => setForm({ ...form, nav_categories: form.nav_categories.filter((_, idx) => idx !== i) })}
              className="text-zinc-400 hover:text-[var(--bs-red)]"
              data-testid={`btn-remove-nav-${i}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </section>

      {/* HOME CATEGORIES */}
      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div><h2 className="font-display font-bold text-xl text-white">Categorie in home (card grandi)</h2><p className="text-xs text-zinc-500 mt-1">Scegli una categoria già usata nei prodotti e carica la sua immagine.</p></div>
          <button
            onClick={() => setForm({ ...form, home_categories: [...form.home_categories, { name: "", image: "" }] })}
            data-testid="btn-add-homecat"
            className="text-xs uppercase tracking-widest border border-zinc-700 px-3 py-2 hover:border-[var(--bs-red)] inline-flex items-center gap-1"
          >
            <Plus size={12} /> Aggiungi
          </button>
        </div>
        {form.home_categories.map((c, i) => (
          <div key={i} className="border border-zinc-900 p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-start" data-testid={`homecat-row-${i}`}>
            <div className="space-y-2">
              <select
                value={c.name}
                onChange={(e) => updateHomeCat(i, "name", e.target.value)}
                className="w-full rounded-none bg-zinc-900 border border-zinc-800 text-white px-3 py-2"
                data-testid={`homecat-name-${i}`}
              >
                <option value="">Scegli la categoria…</option>
                {c.name && !productCategories.includes(c.name) && <option value={c.name}>{c.name}</option>}
                {productCategories.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
              <ImageField value={c.image} onChange={(v) => updateHomeCat(i, "image", v)} testid={`homecat-image-${i}`} />
            </div>
            <div />
            <button
              onClick={() => setForm({ ...form, home_categories: form.home_categories.filter((_, idx) => idx !== i) })}
              className="text-zinc-400 hover:text-[var(--bs-red)]"
              data-testid={`btn-remove-homecat-${i}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </section>

      {/* USED MARKET */}
      <section className="bg-zinc-950 border border-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-white">Mercatino dell'usato</h2>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <span className={`text-xs uppercase tracking-widest ${form.used_market?.enabled ? "text-green-500" : "text-zinc-500"}`}>
              {form.used_market?.enabled ? "Attivo" : "Disattivato"}
            </span>
            <input
              type="checkbox"
              data-testid="cfg-used-enabled"
              checked={!!form.used_market?.enabled}
              onChange={(e) => setForm({ ...form, used_market: { ...(form.used_market || {}), enabled: e.target.checked } })}
              className="w-5 h-5 accent-[var(--bs-red)]"
            />
          </label>
        </div>
        <div className="text-xs text-zinc-500">
          Quando attivo, appare la voce "Mercatino" nel menù e la pagina <span className="text-white">/mercatino</span> con le due sezioni Rivisionato e NON Rivisionato.
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Titolo pagina</label>
          <Input
            data-testid="cfg-used-title"
            value={form.used_market?.title || ""}
            onChange={(e) => setForm({ ...form, used_market: { ...(form.used_market || {}), title: e.target.value } })}
            className="rounded-none bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Sottotitolo</label>
          <Textarea
            data-testid="cfg-used-subtitle"
            value={form.used_market?.subtitle || ""}
            onChange={(e) => setForm({ ...form, used_market: { ...(form.used_market || {}), subtitle: e.target.value } })}
            rows={2}
            className="rounded-none bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Immagine hero</label>
          <ImageField
            value={form.used_market?.hero_image || ""}
            onChange={(v) => setForm({ ...form, used_market: { ...(form.used_market || {}), hero_image: v } })}
            testid="cfg-used-image"
          />
        </div>
      </section>

      <div className="pb-8">
        <button
          onClick={save}
          disabled={saving}
          data-testid="btn-save-settings-bottom"
          className="bs-btn-primary px-8 py-4 text-sm uppercase tracking-widest font-bold disabled:opacity-60"
        >
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}